/**
 * @file useAutoDebugger.js
 * @description Master hook that wires all global interceptors.
 *              Patches: window.onerror, unhandledRejection, console methods,
 *              fetch (network), React render errors (via ErrorBoundary),
 *              and route changes.
 *              ⚠️  Only runs in development. No-ops in production.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { push, EVENT_TYPES } from './debuggerStore';

const IS_DEV = import.meta.env.DEV;

// ─── Known analytics / telemetry domains to silently skip ─────────────────────
// These are third-party beacons (GA4, GTM, Meta Pixel, etc.) that use no-cors
// sendBeacon — they intentionally return opaque status 0. Not your bugs.
const SILENT_DOMAINS = [
    'google-analytics.com',
    'analytics.google.com',
    'googletagmanager.com',
    'googleadservices.com',
    'googlesyndication.com',
    'doubleclick.net',
    'facebook.com/tr',
    'connect.facebook.net',
    'hotjar.com',
    'clarity.ms',
    'segment.io',
    'sentry.io',
    'rollbar.com',
    'fullstory.com',
    'whatconverts.com',
];

function isSilentDomain(url) {
    return SILENT_DOMAINS.some((domain) => url.includes(domain));
}

// ─── Severity classifier ──────────────────────────────────────────────────────
// Status 0 = opaque no-cors response (sendBeacon, import.meta, etc.) — NOT an error
function classifyNetworkSeverity(status, url) {
    if (status === 0) return 'info';    // opaque response — expected for beacons
    if (!status) return 'critical';     // null = true network failure
    if (status >= 500) return 'error';
    if (status >= 400) return 'warning';
    return 'info';
}

// ─── Fetch interceptor (singleton guard) ─────────────────────────────────────
let fetchPatched = false;

function patchFetch() {
    if (fetchPatched || typeof window === 'undefined') return;
    fetchPatched = true;

    const _originalFetch = window.fetch.bind(window);

    window.fetch = async function patchedFetch(input, init = {}) {
        const url = typeof input === 'string' ? input : input?.url ?? '?';
        const method = (init.method ?? 'GET').toUpperCase();
        const t0 = performance.now();

        // Skip known analytics/telemetry beacons — they're not your bugs
        if (isSilentDomain(url)) {
            return _originalFetch(input, init);
        }

        try {
            const res = await _originalFetch(input, init);
            const elapsed = Math.round(performance.now() - t0);
            const severity = classifyNetworkSeverity(res.status, url);

            // Only log non-info network events to reduce noise
            // Status 0 + no-cors = opaque beacon, skip entirely
            if (res.status === 0 && (init.mode === 'no-cors' || res.type === 'opaque')) {
                return res;
            }

            push(EVENT_TYPES.NETWORK, {
                severity,
                method,
                url,
                status: res.status,
                elapsed,
                message: `${method} ${url} → ${res.status || 'opaque'} (${elapsed}ms)`,
            });

            return res;
        } catch (err) {
            const elapsed = Math.round(performance.now() - t0);
            push(EVENT_TYPES.NETWORK, {
                severity: 'critical',
                method,
                url,
                status: null,
                elapsed,
                message: `${method} ${url} → FAILED (${err.message})`,
                stack: err.stack,
            });
            throw err;
        }
    };
}

// ─── Console noise blocklist ──────────────────────────────────────────────────
// These are browser-level, extension-level, or third-party warnings that are
// not caused by application code and carry zero actionable signal.
// Any console.warn / console.error whose text matches a pattern below is
// silently passed through to the real console but NOT recorded in the debugger.
const CONSOLE_NOISE_PATTERNS = [
    // GTM / Analytics sandboxed iframes (browser security policy — not your code)
    /unable to access iframe/i,
    /blocked a frame with origin/i,
    /failed to read a named property.*from.*window/i,
    /cross-origin frame/i,
    /securityerror.*document.*window/i,
    /buildDomTree/i,

    // Vite HMR + dev-server chatter
    /\[vite\]/i,
    /\[HMR\]/i,
    /websocket.*vite/i,

    // React strict-mode double-invoke noise
    /did not expect server html/i,

    // Browser extension injections
    /chrome-extension:\/\//i,
    /moz-extension:\/\//i,

    // Common GTM/GA consent-mode log spam
    /gtm\.js/i,
    /google tag/i,
];

function isConsoleNoise(text) {
    return CONSOLE_NOISE_PATTERNS.some((pattern) => pattern.test(text));
}

// ─── Console interceptor ──────────────────────────────────────────────────────
let consolePatched = false;
const _origConsole = {};

function patchConsole() {
    if (consolePatched) return;
    consolePatched = true;

    ['warn', 'error'].forEach((level) => {
        _origConsole[level] = console[level].bind(console);

        console[level] = function (...args) {
            _origConsole[level](...args); // always preserve native console output

            const text = args
                .map((a) => {
                    if (a instanceof Error) return `${a.message}\n${a.stack}`;
                    if (typeof a === 'object') {
                        try { return JSON.stringify(a, null, 2); } catch { return String(a); }
                    }
                    return String(a);
                })
                .join(' ');

            // Skip known browser/third-party noise — not actionable for your code
            if (isConsoleNoise(text)) return;

            push(level === 'error' ? EVENT_TYPES.ERROR : EVENT_TYPES.WARNING, {
                severity: level === 'error' ? 'error' : 'warning',
                message: text.slice(0, 500),
                source: 'console',
            });
        };
    });
}

function unpatchConsole() {
    ['warn', 'error'].forEach((level) => {
        if (_origConsole[level]) console[level] = _origConsole[level];
    });
    consolePatched = false;
}

// ─── Global error handlers ────────────────────────────────────────────────────
function installGlobalHandlers() {
    const handleError = (event) => {
        push(EVENT_TYPES.ERROR, {
            severity: 'critical',
            message: event.message ?? 'Unknown error',
            source: event.filename ? `${event.filename}:${event.lineno}` : 'window.onerror',
            stack: event.error?.stack,
        });
    };

    const handleRejection = (event) => {
        const reason = event.reason;
        push(EVENT_TYPES.ERROR, {
            severity: 'critical',
            message: reason instanceof Error ? reason.message : String(reason),
            source: 'unhandledRejection',
            stack: reason instanceof Error ? reason.stack : undefined,
        });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
        window.removeEventListener('error', handleError);
        window.removeEventListener('unhandledrejection', handleRejection);
    };
}

// ─── Performance observer ─────────────────────────────────────────────────────
function installPerfObserver() {
    if (typeof PerformanceObserver === 'undefined') return () => { };

    const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
            if (entry.entryType === 'longtask') {
                push(EVENT_TYPES.PERF, {
                    severity: entry.duration > 200 ? 'error' : 'warning',
                    message: `Long task detected: ${Math.round(entry.duration)}ms`,
                    duration: Math.round(entry.duration),
                    source: 'PerformanceObserver',
                });
            }

            if (entry.entryType === 'largest-contentful-paint') {
                push(EVENT_TYPES.PERF, {
                    severity: entry.startTime > 2500 ? 'warning' : 'info',
                    message: `LCP: ${Math.round(entry.startTime)}ms${entry.startTime > 2500 ? ' ⚠️ Above 2.5s threshold' : ' ✓'}`,
                    duration: Math.round(entry.startTime),
                    source: 'LCP',
                });
            }

            if (entry.entryType === 'layout-shift' && entry.value > 0.1) {
                push(EVENT_TYPES.PERF, {
                    severity: 'warning',
                    message: `CLS shift: ${entry.value.toFixed(4)} — check layout stability`,
                    source: 'CLS',
                });
            }
        }
    });

    try {
        observer.observe({ entryTypes: ['longtask', 'largest-contentful-paint', 'layout-shift'] });
    } catch (_e) {
        // Some browsers don't support all entry types — fail silently
    }

    return () => observer.disconnect();
}

// ─── The hook ─────────────────────────────────────────────────────────────────
export function useAutoDebugger() {
    const location = useLocation();
    const firstRender = useRef(true);

    useEffect(() => {
        if (!IS_DEV) return; // ← production guard

        patchFetch();
        patchConsole();

        const cleanupGlobal = installGlobalHandlers();
        const cleanupPerf = installPerfObserver();

        return () => {
            cleanupGlobal();
            cleanupPerf();
            unpatchConsole();
        };
    }, []); // mount-only — all interceptors use module-level singleton guards

    // Track route changes
    useEffect(() => {
        if (!IS_DEV) return;
        if (firstRender.current) {
            firstRender.current = false;
            push(EVENT_TYPES.ROUTE, {
                severity: 'info',
                message: `Initial route: ${location.pathname}`,
                path: location.pathname,
            });
            return;
        }
        push(EVENT_TYPES.ROUTE, {
            severity: 'info',
            message: `Route → ${location.pathname}`,
            path: location.pathname,
        });
    }, [location.pathname]);
}

// ─── React Error Boundary helper ──────────────────────────────────────────────
export function reportReactError(error, errorInfo) {
    if (!IS_DEV) return;
    push(EVENT_TYPES.REACT, {
        severity: 'critical',
        message: error?.message ?? 'React render error',
        stack: error?.stack,
        componentStack: errorInfo?.componentStack?.slice(0, 800),
        source: 'ErrorBoundary',
    });
}
